import { useState, useEffect, useCallback } from 'react';
import {
  getPolicy,
  getSubscriptionStatus,
  createCheckout,
  createPortalSession,
  TierPolicy,
  SubscriptionStatus,
} from '../services/paymentsApi';
import { manageMode, externalManagementUrl } from '../services/subscriptionManagement';

export function useSubscription() {
  const [policy, setPolicy] = useState<TierPolicy | null>(null);
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      // Policy drives access; status carries billing management. Apply them
      // independently so either endpoint can fail without discarding the other.
      await Promise.all([
        getPolicy().then(setPolicy).catch(() => undefined),
        getSubscriptionStatus().then(setStatus).catch(() => undefined),
      ]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Free is the current product default. Be optimistic while policy loads so
  // an offline request cannot flash an obsolete upgrade prompt.
  const freeAccessEnabled = policy?.free_access_enabled
    ?? status?.free_access_enabled
    ?? true;
  const isPlus = policy?.full_access
    ?? (freeAccessEnabled || status?.is_active === true);
  const isFree = !isPlus;
  const isPaidSubscriber = status?.is_active ?? false;
  const subscriptionSource = status?.subscription_source ?? null;

  const handleUpgrade = useCallback(async (plan: 'monthly' | 'annual' = 'monthly') => {
    try {
      const { checkout_url } = await createCheckout(plan);
      window.location.href = checkout_url;
    } catch (err) {
      console.error('Failed to create checkout:', err);
    }
  }, []);

  const handleManage = useCallback(async () => {
    try {
      const { portal_url } = await createPortalSession();
      window.location.href = portal_url;
    } catch (err) {
      console.error('Failed to open customer portal:', err);
    }
  }, []);

  // Provider-aware management: Stripe → portal, app-store → store page, manual
  // (founding/no provider) → no action (UI surfaces a support note instead).
  const manageSubscription = useCallback(async () => {
    const mode = manageMode(subscriptionSource);
    if (mode === 'stripe_portal') return handleManage();
    if (mode === 'app_store') {
      const url = externalManagementUrl(subscriptionSource);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    }
    // 'support' → no-op; the management surface shows the support contact.
  }, [subscriptionSource, handleManage]);

  return {
    policy,
    status,
    loading,
    isPlus,
    isFree,
    isPaidSubscriber,
    freeAccessEnabled,
    subscriptionSource,
    allowedModels: policy?.allowed_models ?? ['gpt-4o'],
    chatLimit: policy?.chat_limit ?? null,
    planLimit: policy?.plan_limit ?? null,
    planCount: policy?.plan_count ?? 0,
    handleUpgrade,
    handleManage,
    manageSubscription,
    refresh,
  };
}
